import { useState, useEffect } from "react";
import { Outlet, useNavigate, useMatches } from "react-router-dom";
import { Spin } from "antd";
import Navigation from "Components/Navigation/Navigation";
import Header from "Components/Header/Header";
import AppBreadcrumb from "Components/AppBreadcrumb/AppBreadcrumb";
import Forbidden from "Components/Forbidden/Forbidden";
import NotFound from "Components/404/404";
import { BreadcrumbProvider } from "contexts/BreadcrumbContext";
import { isRouteAllowed } from "commons/helper";
import useAuthStore from "stores/AuthStore/AuthStore";
import useDiscountCodesConfig from "hooks/useDiscountCodesConfig/useDiscountCodesConfig";

const MainLayout = () => {
  const { userData, isRootUser, isOwner } = useAuthStore();
  const matches = useMatches();
  const [hasHydrated, setHasHydrated] = useState(() =>
    useAuthStore.persist.hasHydrated()
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();

  const { enabled: codesEnabled, loading: codesLoading } =
    useDiscountCodesConfig();

  // Check all matched routes — if any ancestor is rootOnly, block access
  const isAllowed = matches.every((m) =>
    isRouteAllowed(m.handle ?? {}, { isRootUser, isOwner, codesEnabled })
  );
  // Discount codes pages while the switch is off (or unknown) show the 404
  // page, not Forbidden (ui-spec §0.1); wait for the switch before deciding.
  const needsCodes = matches.some((m) => m.handle?.requiresCodesEnabled);
  const codesBlocked = needsCodes && !codesEnabled;

  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!userData) {
      navigate("/login", { replace: true });
    }
  }, [userData, hasHydrated, navigate]);

  if (!hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-10 w-10 animate-spin rounded-full border-3 border-accent border-t-transparent" />
          <p className="text-sm text-muted-foreground">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <BreadcrumbProvider>
      <div className="flex min-h-screen bg-muted/30">
        <Navigation
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isMobileOpen={isMobileOpen}
          setIsMobileOpen={setIsMobileOpen}
        />
        <div
          className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ${isCollapsed ? "lg:ml-16" : "lg:ml-64"}`}
        >
          <Header onMenuClick={() => setIsMobileOpen(true)} />
          <main className="flex-1 p-4 md:p-4">
            <div className="mx-auto  animate-in">
              <AppBreadcrumb />
              {needsCodes && codesLoading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <Spin size="large" />
                </div>
              ) : codesBlocked ? (
                <NotFound />
              ) : isAllowed ? (
                <Outlet />
              ) : (
                <Forbidden />
              )}
            </div>
          </main>
        </div>
      </div>
    </BreadcrumbProvider>
  );
};
export default MainLayout;
